/**
 * EmployeeProfileForm — Story 106-6 (E-106 HR UX Max)
 *
 * 267 LOC monolit 4-panel Accordion-re refaktorálva (E-105 Story 105-2 pattern reuse):
 *  1. Alapadatok (userId, employmentType) — auto-open
 *  2. Munkaviszony (contractStart/End, weeklyHours, salaryType, grossSalary)
 *  3. Bérszámfejtés (TAJ, adószám, bankszámla)
 *  4. Családi adatok (childrenCount, familyTaxCredit, firstMarriage)
 *
 * + Completion-% sticky footer
 * + Panel-szintű validáció (invalid panel auto-expand submit-kor)
 * + Backwards compat: onSubmit API változatlan
 */

import { useTranslation } from '@kgc/i18n';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@kgc/ui';
import { useMemo, useState } from 'react';
import type { CreateEmployeeInput, Employee, EmploymentType, SalaryType } from '../api/hr-api';

interface EmployeeProfileFormProps {
  employee?: Employee | undefined;
  onSubmit: (data: CreateEmployeeInput) => void;
  isLoading?: boolean;
}

const EMPLOYMENT_TYPE_OPTIONS: Array<{ value: EmploymentType; labelKey: string }> = [
  { value: 'FULL_TIME', labelKey: 'hr.employee.employmentTypes.fullTime' },
  { value: 'PART_TIME', labelKey: 'hr.employee.employmentTypes.partTime' },
  { value: 'SEASONAL', labelKey: 'hr.employee.employmentTypes.seasonal' },
];

const SALARY_TYPE_OPTIONS: Array<{ value: SalaryType; labelKey: string }> = [
  { value: 'MONTHLY', labelKey: 'hr.employee.salaryTypes.monthly' },
  { value: 'HOURLY', labelKey: 'hr.employee.salaryTypes.hourly' },
];

type PanelId = 'basic' | 'employment' | 'payroll' | 'family';

export function EmployeeProfileForm({ employee, onSubmit, isLoading }: EmployeeProfileFormProps) {
  const { t } = useTranslation();

  // State (ugyanaz mint korábban)
  const [userId, setUserId] = useState(employee?.userId ?? '');
  const [employmentType, setEmploymentType] = useState<EmploymentType>(
    employee?.employmentType ?? 'FULL_TIME'
  );
  const [contractStart, setContractStart] = useState(
    employee?.contractStart
      ? (new Date(employee.contractStart).toISOString().split('T')[0] ?? '')
      : ''
  );
  const [contractEnd, setContractEnd] = useState(
    employee?.contractEnd ? (new Date(employee.contractEnd).toISOString().split('T')[0] ?? '') : ''
  );
  const [weeklyHours, setWeeklyHours] = useState(String(employee?.weeklyHours ?? 40));
  const [grossSalary, setGrossSalary] = useState(String(employee?.grossSalary ?? ''));
  const [salaryType, setSalaryType] = useState<SalaryType>(employee?.salaryType ?? 'MONTHLY');
  const [bankAccountNumber, setBankAccountNumber] = useState(employee?.bankAccountNumber ?? '');
  const [taxId, setTaxId] = useState(employee?.taxId ?? '');
  const [tajNumber, setTajNumber] = useState(employee?.tajNumber ?? '');
  const [childrenCount, setChildrenCount] = useState(String(employee?.childrenCount ?? 0));
  const [hasFamilyTaxCredit, setHasFamilyTaxCredit] = useState(
    employee?.hasFamilyTaxCredit ?? false
  );
  const [familyCreditChildCount, setFamilyCreditChildCount] = useState(
    String(employee?.familyCreditChildCount ?? 0)
  );

  const [openPanels, setOpenPanels] = useState<string[]>(['basic']);

  // Panel-szintű validáció
  const panelValidity = useMemo(() => {
    const basic = userId.trim() !== '' && employmentType !== undefined;
    const employment = contractStart !== '' && Number(weeklyHours) > 0 && Number(grossSalary) >= 0;
    const payroll = true; // TAJ/adó/bank opcionális
    const family = Number(childrenCount) >= 0;
    return { basic, employment, payroll, family };
  }, [userId, employmentType, contractStart, weeklyHours, grossSalary, childrenCount]);

  // Completion-% súlyozva (kötelező=1, opcionális=0.5)
  const completion = useMemo(() => {
    const fields: Array<{ filled: boolean; weight: number }> = [
      { filled: userId.trim() !== '', weight: 1 },
      { filled: employmentType !== undefined, weight: 1 },
      { filled: contractStart !== '', weight: 1 },
      { filled: Number(weeklyHours) > 0, weight: 1 },
      { filled: Number(grossSalary) > 0, weight: 1 },
      { filled: tajNumber.trim() !== '', weight: 0.5 },
      { filled: taxId.trim() !== '', weight: 0.5 },
      { filled: bankAccountNumber.trim() !== '', weight: 1 },
      { filled: contractEnd !== '', weight: 0.5 },
      { filled: childrenCount !== '', weight: 0.5 },
    ];
    const totalWeight = fields.reduce((sum, f) => sum + f.weight, 0);
    const filledWeight = fields.reduce((sum, f) => sum + (f.filled ? f.weight : 0), 0);
    return Math.round((filledWeight / totalWeight) * 100);
  }, [
    userId,
    employmentType,
    contractStart,
    weeklyHours,
    grossSalary,
    tajNumber,
    taxId,
    bankAccountNumber,
    contractEnd,
    childrenCount,
  ]);

  const missingFieldLabels = useMemo(() => {
    const labels: string[] = [];
    if (userId.trim() === '') labels.push(t('hr.employee.userId'));
    if (bankAccountNumber.trim() === '') labels.push(t('hr.employee.bankAccount'));
    if (tajNumber.trim() === '') labels.push(t('hr.employee.tajNumber'));
    if (contractStart === '') labels.push(t('hr.employee.contractStart'));
    return labels;
  }, [userId, bankAccountNumber, tajNumber, contractStart, t]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Panel-szintű validáció: az első invalid panel auto-expand
    const invalidPanels: PanelId[] = [];
    if (!panelValidity.basic) invalidPanels.push('basic');
    if (!panelValidity.employment) invalidPanels.push('employment');
    if (!panelValidity.payroll) invalidPanels.push('payroll');
    if (!panelValidity.family) invalidPanels.push('family');

    if (invalidPanels.length > 0) {
      setOpenPanels(invalidPanels);
      toast.error(t('hr.employee.validationError'));
      return;
    }

    onSubmit({
      userId,
      employmentType,
      contractStart,
      contractEnd: contractEnd || undefined,
      weeklyHours: Number(weeklyHours),
      grossSalary: Number(grossSalary),
      salaryType,
      bankAccountNumber: bankAccountNumber || undefined,
      taxId: taxId || undefined,
      tajNumber: tajNumber || undefined,
      childrenCount: Number(childrenCount),
      hasFamilyTaxCredit,
      familyCreditChildCount: Number(familyCreditChildCount),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 pb-24">
      <Accordion type="multiple" value={openPanels} onValueChange={setOpenPanels}>
        {/* Panel 1: Alapadatok */}
        <AccordionItem value="basic">
          <AccordionTrigger>
            <div className="flex items-center gap-2">
              <span>{t('hr.employee.panelBasic')}</span>
              {!panelValidity.basic && (
                <span
                  className="h-2 w-2 rounded-full bg-red-500"
                  aria-label={t('hr.employee.panelInvalid')}
                />
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 pt-2">
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('hr.employee.userId')}</label>
                <Input
                  value={userId}
                  onChange={e => setUserId(e.target.value)}
                  required
                  placeholder="user-uuid"
                  disabled={Boolean(employee)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('hr.employee.employmentType')}</label>
                <Select
                  value={employmentType}
                  onValueChange={val => setEmploymentType(val as EmploymentType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EMPLOYMENT_TYPE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {t(opt.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Panel 2: Munkaviszony */}
        <AccordionItem value="employment">
          <AccordionTrigger>
            <div className="flex items-center gap-2">
              <span>{t('hr.employee.panelEmployment')}</span>
              {!panelValidity.employment && (
                <span
                  className="h-2 w-2 rounded-full bg-red-500"
                  aria-label={t('hr.employee.panelInvalid')}
                />
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 pt-2">
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('hr.employee.contractStart')}</label>
                <Input
                  type="date"
                  value={contractStart}
                  onChange={e => setContractStart(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('hr.employee.contractEnd')}</label>
                <Input
                  type="date"
                  value={contractEnd}
                  min={contractStart}
                  onChange={e => setContractEnd(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('hr.employee.weeklyHours')}</label>
                <Input
                  type="number"
                  value={weeklyHours}
                  min={1}
                  max={60}
                  onChange={e => setWeeklyHours(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('hr.employee.salaryType')}</label>
                <Select value={salaryType} onValueChange={val => setSalaryType(val as SalaryType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SALARY_TYPE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {t(opt.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 md:col-span-2">
                <label className="text-sm font-medium">{t('hr.employee.grossSalary')}</label>
                <Input
                  type="number"
                  value={grossSalary}
                  min={0}
                  onChange={e => setGrossSalary(e.target.value)}
                  required
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Panel 3: Bérszámfejtés */}
        <AccordionItem value="payroll">
          <AccordionTrigger>{t('hr.employee.panelPayroll')}</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 pt-2">
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('hr.employee.tajNumber')}</label>
                <Input
                  value={tajNumber}
                  onChange={e => setTajNumber(e.target.value)}
                  placeholder="000-000-000"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('hr.employee.taxId')}</label>
                <Input
                  value={taxId}
                  onChange={e => setTaxId(e.target.value)}
                  placeholder="0000000000"
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <label className="text-sm font-medium">{t('hr.employee.bankAccount')}</label>
                <Input
                  value={bankAccountNumber}
                  onChange={e => setBankAccountNumber(e.target.value)}
                  placeholder="00000000-00000000-00000000"
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Panel 4: Családi adatok */}
        <AccordionItem value="family">
          <AccordionTrigger>{t('hr.employee.panelFamily')}</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 pt-2">
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('hr.employee.childrenCount')}</label>
                <Input
                  type="number"
                  value={childrenCount}
                  min={0}
                  onChange={e => setChildrenCount(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="familyTaxCredit"
                  checked={hasFamilyTaxCredit}
                  onChange={e => setHasFamilyTaxCredit(e.target.checked)}
                  className="h-4 w-4"
                />
                <label htmlFor="familyTaxCredit" className="text-sm font-medium">
                  {t('hr.employee.familyTaxCredit')}
                </label>
              </div>
              {hasFamilyTaxCredit && (
                <div className="space-y-1 md:col-span-2">
                  <label className="text-sm font-medium">
                    {t('hr.employee.familyCreditChildCount')}
                  </label>
                  <Input
                    type="number"
                    value={familyCreditChildCount}
                    min={0}
                    onChange={e => setFamilyCreditChildCount(e.target.value)}
                  />
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Sticky completion footer */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 backdrop-blur px-4 py-3 shadow-lg">
        <div className="mx-auto max-w-4xl flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">
                {t('hr.employee.completionLabel', { percent: completion })}
              </span>
              {missingFieldLabels.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {t('hr.employee.missingLabel')}: {missingFieldLabels.slice(0, 3).join(', ')}
                  {missingFieldLabels.length > 3 && ' …'}
                </span>
              )}
            </div>
            <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full transition-all ${
                  completion >= 80
                    ? 'bg-green-500'
                    : completion >= 50
                      ? 'bg-yellow-500'
                      : 'bg-red-500'
                }`}
                style={{ width: `${completion}%` }}
              />
            </div>
          </div>
          <Button type="submit" disabled={isLoading} className="sm:ml-4">
            {isLoading ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </div>
    </form>
  );
}
